import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { getLinesFromArrays } from 'app/utils/array';
import { getSortedPlayers } from 'app/utils/entities/match_data';
import { LOL_PLAYERS_ROLE_ORDER } from 'app/config/videogameSpecific.config';
import CountryFlag from 'app/components/CountryFlag';
import Dota2PositionIndicator from 'app/components/Dota2PositionIndicator';
import LoLPlayerRoleIndicator from 'app/components/LoLPlayerRoleIndicator';
import TeamName from 'app/components/TeamName';
import BlockTemplate from 'app/components/Blocks/BlockTemplate';

import style from './style.module.scss';

interface Props {
  videogame: VideogameSlug;
  opponents: OpponentWithStats[] | RosterOpponent[] | undefined;
}

const RostersBlock: FC<Props> = ({ opponents, videogame }) => {
  const { t } = useTranslation();

  const opponent1 = opponents?.at(0);
  const opponent2 = opponents?.at(1);

  const sortedPlayers1 = getSortedPlayers(opponent1);
  const sortedPlayers2 = getSortedPlayers(opponent2);

  const lines = getLinesFromArrays(sortedPlayers1, sortedPlayers2);

  return (
    <BlockTemplate
      title={t('blocks_rosters_title')}
      opponentsLogo={[opponent1, opponent2]}
      containerClassname={style.rostersBlockContent}
      hasData={Boolean(lines.length)}
    >
      <TeamName.Container className={style.teamsNames}>
        <TeamName team={opponent1} logoStyle={style.teamNameLogoSize} />
        <TeamName team={opponent2} logoStyle={style.teamNameLogoSize} orientation="reversed" />
      </TeamName.Container>

      <div>
        {lines.map((line, index) => (
          <div key={index} className={style.line}>
            <div className={style.player}>
              {line[0] && (
                <>
                  <div>{line[0].name}</div>
                  {line[0].country && <CountryFlag country={line[0].country} size={16} />}
                </>
              )}
            </div>
            <div>
              {videogame === 'dota-2' && <Dota2PositionIndicator position={index} />}
              {videogame === 'league-of-legends' && (
                <LoLPlayerRoleIndicator
                  role={LOL_PLAYERS_ROLE_ORDER[index]}
                  className={style.lolRole}
                />
              )}
            </div>
            <div className={style.player}>
              {line[1] && (
                <>
                  <div>{line[1].name}</div>
                  {line[1].country && <CountryFlag country={line[1].country} size={16} />}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </BlockTemplate>
  );
};

RostersBlock.displayName = 'RostersBlock';

export default RostersBlock;
